import React from "react";
import { Selection, IObjectWithKey, SelectionMode } from "@nuorder/components";
import { FolderNavigation, IFolderSelection } from "./folder-navigation";
import { flattenInfiniteData, ROOT_ITEM, SpaceView, fetchMoreDelayed } from "containers/folders/hooks/utils";
import { useSpaceViewFetcher } from "containers/folders/hooks/space-item";
import { ItemKind, OrderByDirection, SpaceItemOrderByField } from "containers/folders/types/base";
import { IFolderSelectionSettings } from "./use-folder-selection";
import { buildRootDrive } from "containers/folders/utils/drive-navigation";
import { IFolderNavigationBreadcrumbProps } from "./folder-navigation-breadcrumb";
import { usePrevious } from "hooks";
import { SHARED_WITH_ME_ROOT_ITEM_ID } from "containers/folders/utils/constants";
import { useFlags } from "launchdarkly-react-client-sdk";
import { connect } from "react-redux";
import { IStore } from "reducers";
import { BrandRecord } from "records/brand";
import { hasVerticalAssortmentAccess } from "containers/folders/utils/vertical-assortments";
import me from "models/me";
import { filterCollectionItems } from "../utils/filter-collection-items";

interface IStateProps {
    brand?: BrandRecord;
}

interface IFolderSelectionProps {
    settings: IFolderSelectionSettings;
    onUpdateSettings: (settings: Partial<IFolderSelectionSettings>) => void;
    onRenderWayfinding?: (props: IFolderNavigationBreadcrumbProps) => React.ReactElement;
    onDisplayAlert?: () => void;
    displayMerchantDrive: boolean;
    displayMyDrive: boolean;
    membership: nuorder.merchant.v1.Member;
    mySpace: nuorder.folder.v1.Space;
    merchantSpace: nuorder.folder.v1.Space;
    item: nuorder.folder.v1.Item;
    errorMessage?: string;
    selectionMode?: SelectionMode;
    selectionKind?: ItemKind;
    noMargin?: boolean;
    itemLimit?: number;
}

export const isConfirmSelectionDisabled = (
    { itemSelected, navigation, multiple }: IFolderSelectionSettings,
    item: nuorder.folder.v1.Item,
    disabled: boolean,
    allowSameParent?: boolean,
    isShortcut?: boolean,
    itemsToBulk?: nuorder.folder.v1.Item[]
) => {
    const itemSelectedNormilized = (multiple ? itemSelected[0] : itemSelected) as IFolderSelection;
    const currentItem = itemSelectedNormilized || navigation[navigation.length - 1];
    const navigationIds = navigation.map((it) => it?.id);
    const isSameItemOrParentSelected = itemsToBulk
        ? itemsToBulk.some((itemToBulk) =>
              validateIfSameItemOrParentIsSelected(itemToBulk, currentItem, navigationIds, allowSameParent)
          )
        : validateIfSameItemOrParentIsSelected(item, currentItem, navigationIds, allowSameParent);
    return (
        disabled ||
        !currentItem ||
        (currentItem.isDrive && currentItem.spaceView === SpaceView.SharedWithMe) ||
        !currentItem.capabilities?.can_add_children ||
        (!isShortcut && isSameItemOrParentSelected)
    );
};

const validateIfSameItemOrParentIsSelected = (item, currentItem, navigationIds, allowSameParent) =>
    item?.id === currentItem?.id ||
    navigationIds.includes(item?.id) ||
    (!allowSameParent && item?.parent === currentItem?.id);

export const FolderSelection: React.FunctionComponent<IStateProps & IFolderSelectionProps> = ({
    settings,
    onUpdateSettings,
    onRenderWayfinding,
    onDisplayAlert,
    displayMerchantDrive,
    membership,
    mySpace,
    merchantSpace,
    item,
    displayMyDrive,
    errorMessage,
    selectionMode = SelectionMode.single,
    selectionKind = ItemKind.Folder,
    noMargin,
    itemLimit,
    brand,
}) => {
    const {
        currentItemId,
        rootItemId,
        currentView,
        navigation,
        itemSelected,
        multiple,
        markAllAsSelected,
        automaticLoad,
    } = settings;
    const [isFetchingFolders, setIsFetchingFolders] = React.useState(true);
    const [folderItems, setFolderItems] = React.useState<nuorder.folder.v1.ListItemsResponse[]>([]);
    const [otherItems, setOtherItems] = React.useState<nuorder.folder.v1.ListItemsResponse[]>([]);

    const selection = React.useMemo<Selection>(
        () =>
            new Selection<IFolderSelection & IObjectWithKey>({
                getKey: (item) => item.id,
                canSelectItem: (item: IFolderSelection) => !item.isDrive && item.kind === selectionKind,
                onSelectionChanged: () => {
                    if (!multiple) {
                        const items = selection.getSelection();

                        onUpdateSettings({
                            itemSelected: items[0] as any,
                        });
                    }
                },
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onUpdateSettings, multiple]
    );

    const resetFetchItems = React.useCallback(() => {
        setIsFetchingFolders(true);
        setFolderItems([]);
        setOtherItems([]);
    }, [setFolderItems, setOtherItems, setIsFetchingFolders]);

    const prevFetchingFolders = usePrevious(isFetchingFolders);
    const useInfiniteFetchItems = useSpaceViewFetcher(currentView, rootItemId, true);
    const {
        isFetching: isFetchingItems,
        isFetchingMore: isFetchingMoreItems,
        fetchMore,
        canFetchMore,
    } = useInfiniteFetchItems(
        membership?.merchant_id,
        currentItemId,
        membership?.member_id,
        {
            filter: {
                item_kind: isFetchingFolders ? ItemKind.Folder : selectionKind,
            },
            order_by: SpaceItemOrderByField.Name,
            order_by_direction: OrderByDirection.Asc,
        },
        {
            enabled: !!rootItemId && !!currentItemId && !!membership && !!currentView,
            onSuccess: (raw: nuorder.folder.v1.ListItemsResponse[]) => {
                const lastResponse = raw[raw.length - 1];
                const shouldSwitch = lastResponse.items?.length === 0 && !lastResponse.next_page_token;
                if (isFetchingFolders) {
                    setFolderItems(shouldSwitch ? raw.splice(0, raw.length - 1) : raw);
                    if (shouldSwitch && selectionKind !== ItemKind.Folder) {
                        setIsFetchingFolders(false);
                    }
                } else {
                    if (multiple && automaticLoad && shouldSwitch) {
                        const _otherItems = raw.length === 1 ? otherItems : raw.splice(0, raw.length - 1);
                        const newItemsToSelect = flattenInfiniteData<
                            nuorder.folder.v1.ListItemsResponse,
                            IFolderSelection
                        >([].concat(_otherItems), (response) => response.items as IFolderSelection[]).filter(
                            (it) =>
                                it.kind === selectionKind &&
                                !(settings.itemSelected as IFolderSelection[]).some((x) => x.id === it.id)
                        );
                        const selectedItems = settings.itemSelected as IFolderSelection[];

                        let displayAlert = false;
                        const newItemsSelected = newItemsToSelect.reduce(
                            (acc, item) => {
                                if (acc.length <= itemLimit - 1) {
                                    const data =
                                        currentItemId === SHARED_WITH_ME_ROOT_ITEM_ID
                                            ? { ...item, isSharedRoot: true }
                                            : item;
                                    acc.push(data as IFolderSelection);
                                } else {
                                    displayAlert = true;
                                }

                                return acc;
                            },
                            [...selectedItems]
                        );

                        onUpdateSettings({
                            automaticLoad: false,
                            itemSelected: newItemsSelected,
                        });

                        displayAlert && onDisplayAlert?.();
                    }
                    if (raw.length === 1 && shouldSwitch) {
                        return;
                    }

                    setOtherItems(shouldSwitch ? raw.splice(0, raw.length - 1) : raw);
                }
            },
        }
    );

    const handleFetchNextResults = React.useCallback(() => {
        fetchMoreDelayed(canFetchMore, isFetchingMoreItems, isFetchingItems, fetchMore);
    }, [canFetchMore, isFetchingMoreItems, isFetchingItems, fetchMore]);

    React.useEffect(() => {
        if (automaticLoad && multiple) {
            handleFetchNextResults();
        }
    }, [handleFetchNextResults, automaticLoad, multiple]);

    const { verticalAssortmentsEnabled } = useFlags();
    const isVerticalAssortmentEnabled = hasVerticalAssortmentAccess(me, brand as any, verticalAssortmentsEnabled);

    const itemsResponse = React.useMemo(() => {
        const allItems = [].concat(folderItems, otherItems);
        const flattenedItems = flattenInfiniteData<nuorder.folder.v1.ListItemsResponse, nuorder.folder.v1.Item>(
            allItems,
            (response) => filterCollectionItems(response.items, isVerticalAssortmentEnabled)
        );

        if (canFetchMore || (prevFetchingFolders && !isFetchingFolders)) {
            return flattenedItems.concat([null]);
        }

        return flattenedItems;
    }, [otherItems, folderItems, canFetchMore, prevFetchingFolders, isFetchingFolders, isVerticalAssortmentEnabled]);

    const getRootDrives = React.useCallback(
        (): IFolderSelection[] => [
            ...(displayMerchantDrive
                ? [
                      buildRootDrive(
                          merchantSpace,
                          SpaceView.MerchantDrive,
                          membership?.merchant_ref?.name ?? membership?.merchant_id
                      ),
                  ]
                : []),
            ...(displayMyDrive ? [buildRootDrive(mySpace, SpaceView.MyDrive)] : []),
            buildRootDrive(null, SpaceView.SharedWithMe),
        ],
        [displayMerchantDrive, displayMyDrive, membership, merchantSpace, mySpace]
    );

    const folders = React.useMemo<IFolderSelection[]>(() => {
        if (!currentView) {
            return getRootDrives();
        }

        if (currentItemId === SHARED_WITH_ME_ROOT_ITEM_ID) {
            return itemsResponse.map((it) => (it ? { ...it, isSharedRoot: true } : it)) as IFolderSelection[];
        }
        return itemsResponse as IFolderSelection[];
    }, [getRootDrives, currentView, itemsResponse, currentItemId]);

    const handleRowSelected = React.useCallback(
        (item: IFolderSelection, checked: boolean) => {
            const newItemsSelected = checked
                ? (itemSelected as IFolderSelection[]).concat(item)
                : (itemSelected as IFolderSelection[]).filter((it) => it.id !== item.id);
            const isValid = itemLimit ? newItemsSelected.length <= itemLimit : true;

            if (isValid) {
                onUpdateSettings({
                    itemSelected: newItemsSelected,
                    markAllAsSelected: markAllAsSelected && checked,
                });
            } else {
                onDisplayAlert?.();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onUpdateSettings, itemSelected, folders, itemLimit, markAllAsSelected, onDisplayAlert]
    );

    const getNavigation = React.useCallback(
        (itemId: string, currentItem: IFolderSelection) => {
            if (itemId === ROOT_ITEM) {
                return [];
            }

            const index = navigation.findIndex((it) => it.id === itemId);
            return index === -1 ? navigation.concat(currentItem) : navigation.slice(0, index + 1);
        },
        [navigation]
    );

    const handleGoToSpaceItem = React.useCallback(
        (itemId: string, spaceView: SpaceView, isBackNavigation: boolean) => {
            const currentItem = (isBackNavigation ? navigation : folders).find((it) => it.id === itemId);
            const newNavigation = getNavigation(itemId, currentItem);
            if (spaceView) {
                onUpdateSettings({
                    currentItemId: itemId,
                    rootItemId: itemId,
                    currentView: spaceView,
                    navigation: newNavigation,
                    markAllAsSelected: false,
                    automaticLoad: false,
                });
            } else if (itemId === ROOT_ITEM) {
                onUpdateSettings({
                    currentItemId: null,
                    rootItemId: null,
                    currentView: null,
                    navigation: newNavigation,
                    markAllAsSelected: false,
                    automaticLoad: false,
                });
            } else {
                onUpdateSettings({
                    currentItemId: itemId,
                    navigation: newNavigation,
                    markAllAsSelected: false,
                    automaticLoad: false,
                });
            }
            resetFetchItems();
        },
        [onUpdateSettings, folders, navigation, getNavigation, resetFetchItems]
    );

    return (
        <FolderNavigation
            selection={selection}
            onGoToSpaceItem={handleGoToSpaceItem}
            onFetchNextResults={handleFetchNextResults}
            onRenderWayfinding={onRenderWayfinding}
            isFetchingData={isFetchingItems}
            folders={folders}
            errorMessage={errorMessage}
            navigation={navigation}
            selectionMode={selectionMode}
            selectionKind={selectionKind}
            noMargin={noMargin}
            onRowSelected={handleRowSelected}
            itemsSelected={itemSelected as IFolderSelection[]}
            canFetchMore={canFetchMore || (prevFetchingFolders && !isFetchingFolders)}
        />
    );
};

const mapStateToProps = (store: IStore): IStateProps => ({
    brand: store.scope.brand,
});

export const FolderSelectionWithRedux = connect<IStateProps, unknown, IFolderSelectionProps>(
    mapStateToProps,
    null
)(FolderSelection);
